/**
 * Register grid — layout rules (AVA-602, showcase B "register-table-ownership-showcase.html").
 *
 * Column order and pinning, the Linked Tags chip window, which row actions are primary, and the
 * one rule that decides whether a linked Tag opens in a new tab.
 *
 * No Vue / Vuetify / `@/` imports: unit-tested with `node --test`.
 */

export const REGISTER_TABLE_COLUMN = Object.freeze({
  SELECT: 'data-table-select',
  ROW_INDEX: 'RowIndex',
  LINKED_TAGS: 'LinkedTags',
  /** Synthetic column: only the central view of a Separated Facility Group register has it. */
  FACILITY: '__facility',
  ACTIONS: 'actions',
});

/** Left-pinned block, in order. Facility is present only when the caller adds it. */
export const LEFT_PINNED_COLUMNS = Object.freeze([
  REGISTER_TABLE_COLUMN.SELECT,
  REGISTER_TABLE_COLUMN.ROW_INDEX,
  REGISTER_TABLE_COLUMN.LINKED_TAGS,
  REGISTER_TABLE_COLUMN.FACILITY,
]);

/** Never released by the pin budget. */
export const ALWAYS_PINNED_COLUMNS = Object.freeze([REGISTER_TABLE_COLUMN.SELECT, REGISTER_TABLE_COLUMN.ROW_INDEX]);

/** Pinned blocks (left + right) may use at most this share of the visible table width. */
export const PIN_BUDGET_RATIO = 0.7;

/** Linked Tags: above THRESHOLD chips a row shows VISIBLE_WHEN_CAPPED plus "Show all". */
export const LINKED_TAG_CHIP_LIMIT = Object.freeze({ THRESHOLD: 5, VISIBLE_WHEN_CAPPED: 4 });

/**
 * Structural column order: select, #, Linked Tags, Facility, then the user's own order for every
 * other column, then Actions last. Enforced here (not in stored column settings) because a saved
 * layout from before AVA-602 still has Actions as the second column.
 * @param {Array<{ value: string }>} headers visible headers in the user's order
 * @param {?{ value: string }} facilityHeader synthetic Facility header, or null
 */
export function orderRegisterTableHeaders(headers, facilityHeader = null) {
  const list = Array.isArray(headers) ? headers.filter(Boolean) : [];
  const byValue = new Map(list.map((header) => [header.value, header]));
  if (facilityHeader) byValue.set(REGISTER_TABLE_COLUMN.FACILITY, facilityHeader);
  const pinned = LEFT_PINNED_COLUMNS.map((value) => byValue.get(value)).filter(Boolean);
  const pinnedValues = new Set(LEFT_PINNED_COLUMNS);
  const middle = list.filter((header) => !pinnedValues.has(header.value)
    && header.value !== REGISTER_TABLE_COLUMN.ACTIONS);
  const actions = byValue.get(REGISTER_TABLE_COLUMN.ACTIONS);
  return [...pinned, ...middle, ...(actions ? [actions] : [])];
}

/**
 * Which columns stay pinned for the current width, and their left offsets.
 * Over budget, the left block releases from the inside out (Facility, then Linked Tags); select
 * and # always stay. If even that is over budget (phones), Actions stops being pinned too.
 * An unmeasured viewport (0) keeps every pin.
 * @param {{ leftColumns: Array<{ key: string, width: number }>, rightWidth?: number,
 *   viewportWidth?: number, budgetRatio?: number }} input left columns in display order
 * @returns {{ left: Array<{ key: string, offset: number }>, edgeKey: string|null, pinRight: boolean }}
 */
export function resolvePinLayout({
  leftColumns = [],
  rightWidth = 0,
  viewportWidth = 0,
  budgetRatio = PIN_BUDGET_RATIO,
} = {}) {
  const always = new Set(ALWAYS_PINNED_COLUMNS);
  const pinned = leftColumns
    .filter((column) => column && typeof column.key === 'string')
    .map((column) => ({ key: column.key, width: Math.max(0, Number(column.width) || 0) }));
  const right = Math.max(0, Number(rightWidth) || 0);
  const budget = viewportWidth > 0 ? viewportWidth * budgetRatio : Infinity;
  const used = () => pinned.reduce((sum, column) => sum + column.width, 0);

  while (pinned.length && !always.has(pinned[pinned.length - 1].key) && used() + right > budget) {
    pinned.pop();
  }
  const pinRight = used() + right <= budget;

  let offset = 0;
  const left = pinned.map((column) => {
    const entry = { key: column.key, offset };
    offset += column.width;
    return entry;
  });
  return { left, edgeKey: left.length ? left[left.length - 1].key : null, pinRight };
}

/**
 * Chip window for one row's Linked Tags: every chip up to THRESHOLD; above it the first
 * VISIBLE_WHEN_CAPPED plus a "+N · Show all M" toggle that expands in place.
 */
export function linkedTagChipWindow(total, expanded = false) {
  const count = Math.max(0, Number.isInteger(total) ? total : 0);
  if (count <= LINKED_TAG_CHIP_LIMIT.THRESHOLD) {
    return { visibleCount: count, hiddenCount: 0, toggle: null, toggleLabel: '' };
  }
  if (expanded) {
    return { visibleCount: count, hiddenCount: 0, toggle: 'collapse', toggleLabel: 'Show fewer' };
  }
  const hiddenCount = count - LINKED_TAG_CHIP_LIMIT.VISIBLE_WHEN_CAPPED;
  return {
    visibleCount: LINKED_TAG_CHIP_LIMIT.VISIBLE_WHEN_CAPPED,
    hiddenCount,
    toggle: 'expand',
    toggleLabel: `+${hiddenCount} · Show all ${count}`,
  };
}

/**
 * The linked-record navigation rule (RegisterTable `openLinkedRecord`): a Tag opens in place only
 * when its navigation Facility is the Facility this grid is showing; anything else - including the
 * central view, whose Facility is null - opens a new tab. Both ids must already be validated.
 */
export function linkedTagOpensNewTab(currentFacilityId, navigationFacilityId) {
  if (typeof navigationFacilityId !== 'string' || !navigationFacilityId) return false;
  if (typeof currentFacilityId !== 'string' || !currentFacilityId) return true;
  return currentFacilityId.toLowerCase() !== navigationFacilityId.toLowerCase();
}

const TAG_TYPE_ICONS = Object.freeze({
  ForgeEquipment: 'mdi-cog-outline',
  ForgePipeline: 'mdi-pipe',
  ForgeInspection: 'mdi-clipboard-check-outline',
  ForgeStructural: 'mdi-home-outline',
  ForgeAnnotation: 'mdi-comment-text-outline',
  ForgeScaffold: 'mdi-ladder',
  ForgePermit: 'mdi-file-document-outline',
  ForgeContainer: 'mdi-archive-outline',
  ForgeAnomaly: 'mdi-alert-circle-outline',
});

/** Tag type icon, same family the rest of the app uses for these discriminators. */
export function tagTypeIcon(discriminator) {
  return TAG_TYPE_ICONS[discriminator] || 'mdi-tag-outline';
}

export const REGISTER_ROW_ACTION = Object.freeze({
  DETAILS: 'details',
  EDIT: 'edit',
  HISTORY: 'history',
  VIEW_3D: 'view3d',
  PID: 'pid',
});

/**
 * At most two primary icons (shown on row hover / focus, always on touch); everything else goes
 * in the ⋮ menu. Same availability rules as before: Edit needs the Update permission, 3D needs a
 * linked Tag, P&ID needs the server's HasPIdDocument flag.
 */
export function registerRowActions({ canEdit = false, hasLinkedTags = false, hasPid = false } = {}) {
  const available = [
    REGISTER_ROW_ACTION.DETAILS,
    ...(canEdit ? [REGISTER_ROW_ACTION.EDIT] : []),
    REGISTER_ROW_ACTION.HISTORY,
    ...(hasLinkedTags ? [REGISTER_ROW_ACTION.VIEW_3D] : []),
    ...(hasPid ? [REGISTER_ROW_ACTION.PID] : []),
  ];
  return { primary: available.slice(0, 2), menu: available.slice(2) };
}
